//////////////////
// IMPORTS
//////////////////

// libraries
import React, { useMemo } from "react";
import { useRouter } from "found";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";

// query
import { useMutation, ApolloError, useLazyQuery } from "@apollo/client";
import {
  SchedulingLinkTimesVariables,
  CreateBooking,
  linkTimesQuery,
  createBookingMutation,
  SchedulingLinkTimes,
} from "@clockwise/web-commons/src/ui/scheduling-link";
import { CreateBookingBody, TimeSlot } from "@clockwise/schema";

// Components
import { SchedulingLink } from "@clockwise/web-commons/src/ui/scheduling-link";
import { SchedulingLinkLoading } from "@clockwise/web-commons/src/ui/scheduling-link-loading";
import { SlotTimeRange } from "@clockwise/web-commons/src/ui/booking-modal/booking-modal";

import BannerVariants from "./banner-variants";

// util
import { getEditSchedulingLinkPath } from "@clockwise/web-commons/src/util/scheduling.util";
import { getTimeZoneGuess } from "@clockwise/client-commons/src/util/time-zone";

// tracking
import { createUUID } from "@clockwise/client-commons/src/util/uuid";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { RouteRenderer } from "@components/route-renderer";
import { getError, getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { paths } from "@clockwise/client-commons/src/constants/site";
import {
  BookedSlot,
  DateString,
} from "@clockwise/web-commons/src/ui/scheduling-link/scheduling-link";
import { chunk, compact, head, last } from "lodash";
import { CreateBookingVariables } from "@clockwise/web-commons/src/ui/scheduling-link/__generated__/CreateBooking";
import { getFullName } from "@clockwise/web-commons/src/util/profile.util";
import { getDateRange } from "@clockwise/web-commons/src/util/date.util";

const getInitialDate = ({
  date,
  time,
  timezone,
}: {
  date: DateString;
  time: string;
  timezone: string;
}) => {
  if (date && time && timezone) {
    return DateTime.fromFormat(`${date}:${time}`, "yyyy-MM-dd:HHmm", {
      zone: timezone,
    })
      .toLocal()
      .toISODate();
  }

  return date || DateTime.now().toISODate();
};

export const SchedulingLinkRenderer = () => {
  const trackEvent = useTracking();
  const {
    router,
    match: {
      params: { linkName, slug },
      location: {
        query: { date, time, timezone },
      },
    },
  } = useRouter();
  const sessionId = React.useMemo(createUUID, []);
  const [currentDate, setCurrentDate] = React.useState<DateString>(
    getInitialDate({ date, time, timezone }),
  );
  const timeZoneGuess = useMemo(() => getTimeZoneGuess(true), []);
  // Last day of the month that the user is currently viewing. Can be different than currentDate.
  const [monthViewing, setMonthViewing] = React.useState<DateString>(() =>
    DateTime.fromISO(currentDate).startOf("month").toISODate(),
  );
  const [monthsFetched, setMonthsFetched] = React.useState<{ [date: string]: boolean }>({});
  const [emptyMonthsFetched, setEmptyMonthsFetched] = React.useState(0);
  const [bookedSlot, setBookedSlot] = React.useState<BookedSlot | null>(null);
  const [bookingError, setBookingError] = React.useState<ApolloError | null>(null);
  const [isInvalidLink, setIsInvalidLink] = React.useState(false);
  const [isLoadingBatched, setLoadingBatched] = React.useState(false);

  const [getSchedulingLinkTimes, { loading, data, error: apolloError, fetchMore }] = useLazyQuery<
    SchedulingLinkTimes,
    SchedulingLinkTimesVariables
  >(linkTimesQuery, {
    fetchPolicy: "cache-and-network", // quick results from cache, true data back from network.
    notifyOnNetworkStatusChange: true, // This is needed to make the loading variable work according to Apollo docs.
    onError: (error) => {
      if (error.message.includes?.("No such scheduling link")) {
        setIsInvalidLink(true);
      }
    },
  });

  React.useEffect(() => {
    const getInitialLinkTimes = async () => {
      await getSchedulingLinkTimes({
        variables: {
          linkName,
          slug,
          body: {
            timeZone: timeZoneGuess,
            dayTimeSlotRange: {
              minSlot: {
                date: currentDate,
                timeSlot: TimeSlot.T_00_00,
              },
              maxSlot: {
                date: currentDate,
                timeSlot: TimeSlot.T_23_30,
              },
            },
          },
        },
      });
    };

    void getInitialLinkTimes();
    // We only want to run this on the inital render
    // We don't need to make the request everytime the currentDate changes.
    // We will have that data from when we receive the response from fetching
    // the months
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkData = React.useMemo(() => getValue(data?.viewer.schedulingLinkDetails), [
    data?.viewer.schedulingLinkDetails,
  ]);

  const error = getError(data?.viewer.schedulingLinkDetails) || apolloError;

  // Create a tracking events to pass into the view component, prefilled with the linkUrl and a sessionId.
  const trackSession = React.useCallback(
    (event: string, data: Record<string, string | object | number | boolean>) => {
      trackEvent(event, {
        sessionId,
        meetingSettingsId: linkData?.publicMeetingSettings?.id,
        linkUrl: window?.location?.href,
        userAgent: window?.navigator?.userAgent,
        ...data,
      });
    },
    [trackEvent, sessionId, linkData?.publicMeetingSettings?.id],
  );

  const [createBooking, { loading: bookingLoading }] = useMutation<
    CreateBooking,
    CreateBookingVariables
  >(createBookingMutation, {
    onCompleted: (createBookingResponse: CreateBooking) => {
      if (!createBookingResponse || !createBookingResponse.createBooking) {
        // This really shouldn't happen.
        return;
      }
      setBookedSlot(createBookingResponse.createBooking);
      window.history.replaceState("booked", "", `/c/${linkName}/${slug}`);
    },
    onError: (error) => {
      setBookingError(error);
    },
  });

  const fetchRangeBatched = React.useCallback(
    async (start: DateTime, end: DateTime) => {
      // Can't fetch more than 2 weeks at a time, so batch time range into one week slivers.
      const allDates = getDateRange(start.toISODate(), end.toISODate());

      await Promise.all(
        chunk(allDates, 8).map((range) => {
          const start = head(range);
          const end = last(range);

          return fetchMore({
            variables: {
              body: {
                timeZone: timeZoneGuess,
                dayTimeSlotRange: {
                  minSlot: {
                    date: start,
                    timeSlot: TimeSlot.T_00_00,
                  },
                  maxSlot: {
                    date: end,
                    timeSlot: TimeSlot.T_23_30,
                  },
                },
              },
            },
          });
        }),
      );
    },
    [fetchMore, timeZoneGuess],
  );

  const endOfAvailability = React.useMemo(() => {
    return (
      linkData &&
      DateTime.fromISO(linkData?.publicMeetingSettings.endOfAvailability.dateOrDateTime).toISODate()
    );
  }, [linkData]);

  // Fetch a given month denoted by its startDate.
  const fetchMonth = React.useCallback(
    async (startDate: DateString) => {
      if (!endOfAvailability) {
        return;
      }

      const toFetchStartRange = DateTime.max(DateTime.fromISO(startDate), DateTime.now());
      // if the start of the fetch range is passed the end of availability
      // we want to return and not make the request
      if (toFetchStartRange > DateTime.fromISO(endOfAvailability)) {
        return;
      }

      const startRangeFormatted = toFetchStartRange.toISODate();

      if (!monthsFetched[startRangeFormatted]) {
        await fetchRangeBatched(toFetchStartRange, toFetchStartRange.endOf("month"));
        setMonthsFetched((prevMonthsFetched) => {
          return {
            ...prevMonthsFetched,
            [startRangeFormatted]: true,
          };
        });
      }
    },
    [endOfAvailability, fetchRangeBatched, monthsFetched],
  );

  React.useEffect(() => {
    const getMonths = async () => {
      setLoadingBatched(true);
      await Promise.all([
        fetchMonth(monthViewing),
        fetchMonth(DateTime.fromISO(monthViewing).plus({ month: 1 }).startOf("month").toISO()),
      ]);
      setLoadingBatched(false);
    };

    void getMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthViewing, endOfAvailability]);

  const currentDateIndex = React.useMemo(() => {
    if (!currentDate) return 0;

    const currentDateTime = DateTime.fromISO(currentDate);
    return linkData?.suggestedSlotsByDate.findIndex(
      (day) =>
        day.suggestedSlots.length && DateTime.fromISO(day.date).hasSame(currentDateTime, "day"),
    );
  }, [currentDate, linkData]);

  // Find the first available day and set as currentDate. If next batch of data all empty, fetch another month.
  React.useEffect(() => {
    if (linkData && !isLoadingBatched) {
      const startIndex = currentDateIndex && currentDateIndex > 0 ? currentDateIndex : 0;
      // gets the next avaialble time considering date could be in future
      const firstAvailableDate = linkData.suggestedSlotsByDate
        .slice(startIndex)
        .find((byDate) => byDate.suggestedSlots.length);

      if (firstAvailableDate && firstAvailableDate.date !== currentDate) {
        setCurrentDate(firstAvailableDate.date);
      } else {
        // Fetch more times.
        const fetchedMonths = Object.keys(monthsFetched).sort((month1, month2) =>
          month1 > month2 ? -1 : 1,
        ); // Sort fetched months ascending.
        if (fetchedMonths.length && emptyMonthsFetched < 4) {
          // Set the month viewing to next month, to trigger it to get fetched.
          const lastFetchedMonth = last(fetchedMonths) as string; // fetchMonths has as least one element, so this can't be undefined.
          setMonthViewing(
            DateTime.fromISO(lastFetchedMonth).plus({ month: 1 }).startOf("month").toISODate(),
          );
          setEmptyMonthsFetched(emptyMonthsFetched + 1);
        }
      }
    }
  }, [
    currentDate,
    currentDateIndex,
    date,
    emptyMonthsFetched,
    isLoadingBatched,
    linkData,
    monthsFetched,
  ]);

  const onCreateBooking = React.useCallback(
    (attendeeName: string, attendeeEmailAddress: string, selectedTime: SlotTimeRange) => {
      if (!linkData) {
        // If linkData is not set, we can't get durationMinutes. Should never happen.
        return;
      }

      const zone = selectedTime.startTime.timeZone || "local"; // This should not happen but this in case.
      const startTime = selectedTime.startTime.dateOrDateTime;
      const endTime = selectedTime.endTime.dateOrDateTime;

      // Generate timeSlotMin.
      // There is something going wrong with fetching times across timezones. Fetching a small range is
      // not working as expected, so until we have a more specific fix, I want to just broaden this refetch range.
      // This is just used by Webserver to confirm that the time is still available. Broadening this range only results in more work
      // for our server, which is okay.
      const startISODate = DateTime.fromISO(startTime, { zone }).minus({ days: 1 }).toISODate();

      // Generate timeSlotMax.
      // There is something going wrong with fetching times across timezones. Fetching a small range is
      // not working as expected, so until we have a more specific fix, I want to just broaden this refetch range.
      // This is just used by Webserver to confirm that the time is still available. Broadening this range only results in more work
      // for our server, which is okay.
      const endISODate = DateTime.fromISO(endTime, { zone }).plus({ days: 1 }).toISODate();

      const dateRefetchBody = {
        timeZone: zone,
        dayTimeSlotRange: {
          minSlot: {
            date: startISODate,
            timeSlot: TimeSlot.T_00_00,
          },
          maxSlot: {
            date: endISODate,
            timeSlot: TimeSlot.T_23_30,
          },
        },
      };

      const body: CreateBookingBody = {
        attendeeName,
        attendeeEmailAddress,
        eventStartMillis: selectedTime.startTime.millis,
        timeZone: zone,
      };

      void createBooking({
        variables: {
          input: {
            linkName,
            slug,
            body,
            dateRefetchBody,
          },
        },
      });
      trackEvent(TrackingEvents.LINKS.BOOKING.MODAL_BOOK, {
        bookingData: body,
        meetingSettingsId: linkData.publicMeetingSettings.id,
      });
    },
    [createBooking, linkData, linkName, slug, trackEvent],
  );

  React.useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  const isOwner = linkName === getValue(data?.viewer.user?.linkNameErrorable)?.linkName;
  const userEmail = data?.viewer.user?.emails?.[0] ?? null;
  const ownerFullName = getFullName(linkData?.ownerProfile);
  const title = linkData
    ? compact([linkData.publicMeetingSettings.name, ownerFullName]).join(" with ")
    : null;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        {title && <title>{title}</title>}
      </Helmet>
      <RouteRenderer
        variant="scheduling-link"
        error={!!error && !loading}
        notFound={isInvalidLink}
        loading={false /* handled by SchedulingLinkLoading */}
      >
        {!error && linkData ? (
          <SchedulingLink
            linkData={linkData}
            ownerEmail={isOwner ? userEmail : null}
            isOwner={isOwner}
            slug={slug}
            timeZone={timeZoneGuess}
            days={linkData.suggestedSlotsByDate}
            setCurrentDate={setCurrentDate}
            setMonthViewing={setMonthViewing}
            currentDate={currentDate}
            loading={loading || isLoadingBatched}
            onBook={onCreateBooking}
            bookingError={bookingError && bookingError.toString?.()}
            clearBookingError={async (date: string) => {
              if (bookingError) {
                const dateToRefetch = DateTime.fromISO(date, { setZone: true });
                // Refetch the date booked to remove the slots no longer available.
                await fetchRangeBatched(dateToRefetch, dateToRefetch);
                setBookingError(null);
              }
            }}
            bookingLoading={bookingLoading}
            bookedSlot={(!bookingError && !bookingLoading && bookedSlot) || null}
            trackSession={trackSession}
            highlightBestTimes={linkData.publicMeetingSettings.highlightBestTimes}
            endOfAvailability={linkData.publicMeetingSettings.endOfAvailability.dateOrDateTime}
            onEditLink={() => router.push(getEditSchedulingLinkPath(slug))}
            onBack={() => router.push(paths.schedulingLinks)}
            banner={
              <BannerVariants
                ownerProfile={linkData.ownerProfile}
                isAuthenticatedUser={!!getValue(data?.viewer?.user)?.id}
              />
            }
          />
        ) : (
          <SchedulingLinkLoading />
        )}
      </RouteRenderer>
    </>
  );
};
